import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { metaTagCategories } from '../data/metaTags';

const { FiX, FiInfo } = FiIcons;

const MetaTagModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        onClick={onClose}
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" 
      />
      
      <motion.div 
        initial={{ scale: 0.95, opacity: 0, y: 20 }} 
        animate={{ scale: 1, opacity: 1, y: 0 }} 
        exit={{ scale: 0.95, opacity: 0, y: 20 }} 
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-stone-200"
      >
        <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-orange-50/50 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-stone-800 flex items-center gap-2">
              <SafeIcon name="BookOpen" className="text-orange-600" /> 
              Suno Meta-Tag Dictionary
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Advanced production, vocal, and dynamic tags to use inside `[ ]` brackets.
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-stone-200/50 rounded-full transition-colors text-stone-500"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto bg-stone-50 flex-grow">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-8 flex items-start gap-3">
            <SafeIcon icon={FiInfo} className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>How to use:</strong> These tags control the AI's musical arrangement. You can use them standalone like <code>[crescendo]</code> or stack them with section names using a pipe separator: <code>[Chorus | crescendo | layering: vocal stack]</code>.</p>
            </div>
          </div>

          <div className="space-y-8">
            {metaTagCategories.map((category, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
                <div className="bg-stone-50 px-6 py-4 border-b border-stone-100 flex items-center gap-3">
                  <SafeIcon name={category.icon} className="text-orange-500 text-lg" />
                  <h3 className="font-bold text-stone-800 text-lg">{category.title}</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {category.tags.map((tag, tIdx) => (
                    <div key={tIdx} className="p-6 hover:bg-orange-50/30 transition-colors">
                      <div className="flex flex-wrap items-baseline gap-3 mb-2">
                        <span className="text-orange-700 font-mono font-bold bg-orange-100/50 px-2 py-0.5 rounded text-sm">
                          {tag.name}
                        </span>
                      </div>
                      <p className="text-stone-700 mb-3">{tag.desc}</p>
                      
                      {tag.values && (
                        <div className="mb-2 text-sm">
                          <span className="font-semibold text-stone-500">Common values: </span>
                          <span className="text-stone-600">{tag.values}</span>
                        </div>
                      )}
                      
                      <div className="text-sm bg-stone-100/50 p-3 rounded-lg border border-stone-200/60 font-mono text-stone-600">
                        <span className="text-stone-400 select-none mr-2">Example:</span>
                        {tag.example}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MetaTagModal;